import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ProjectBadges from '../common/ProjectBadges';
import './FileExplorer.css';

/**
 * FileExplorer - Shared component for displaying files and folders
 * Used by both Library and Knowledge Center for UI consistency
 * Supports drag-and-drop for moving files and folders
 */
const FileExplorer = ({
  folders,
  documents,
  viewMode = 'list',
  canManage,
  onFolderClick,
  onFileClick,
  onFolderContextMenu,
  onFileContextMenu,
  onFolderAction, // Callback for folder actions: (action, folder) => void
  onFileAction, // Callback for file actions: (action, doc) => void
  onMove, // Callback for drag-drop: (itemType, itemId, targetFolderId) => Promise
  getFileIcon,
  formatDate,
  formatFileSize,
  loading,
  emptyMessage = 'No files or folders',
}) => {
  const [dragOverFolderId, setDragOverFolderId] = useState(null);
  const [draggingItem, setDraggingItem] = useState(null);

  // Drag handlers
  const handleDragStart = (e, item, itemType) => {
    if (!canManage || !onMove) return;
    setDraggingItem({ item, itemType });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify({ itemType, itemId: item.id }));
  };

  const handleDragEnd = () => {
    setDraggingItem(null);
    setDragOverFolderId(null);
  };

  const handleDragOver = (e, folder) => {
    if (!canManage || !onMove || !draggingItem) return;
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent dropping folder into itself or its own child
    if (draggingItem.itemType === 'folder' && draggingItem.item.id === folder.id) {
      return;
    }
    
    setDragOverFolderId(folder.id);
  };

  const handleDragLeave = (e) => {
    if (!canManage || !onMove) return;
    e.preventDefault();
    setDragOverFolderId(null);
  };

  const handleDrop = async (e, targetFolder) => {
    if (!canManage || !onMove) return;
    e.preventDefault();
    e.stopPropagation();
    
    setDragOverFolderId(null);
    
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      if (data.itemType && data.itemId) {
        await onMove(data.itemType, data.itemId, targetFolder.id);
      }
    } catch (err) {
      console.error('Drop error:', err);
    }
    
    setDraggingItem(null);
  };

  if (loading) {
    return (
      <div className="file-explorer-loading">
        <span className="spinner"></span>
        <p>Loading...</p>
      </div>
    );
  }

  if (folders.length === 0 && documents.length === 0) {
    return (
      <div className="file-explorer-empty">
        <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 16h48v32H8V16z" stroke="currentColor" strokeWidth="2" fill="none" />
          <path d="M8 16l24 16 24-16" stroke="currentColor" strokeWidth="2" fill="none" />
        </svg>
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={`file-explorer file-explorer-${viewMode}`}>
      {/* Folders */}
      {folders.length > 0 && (
        <div className="file-explorer-section">
          {folders.map((folder) => (
            <div
              key={`folder-${folder.id}`}
              className={`file-explorer-item folder-item ${dragOverFolderId === folder.id ? 'drag-over' : ''}`}
              onClick={() => onFolderClick(folder)}
              onContextMenu={(e) => {
                if (canManage && onFolderContextMenu) onFolderContextMenu(e, folder);
              }}
              draggable={canManage && !!onMove}
              onDragStart={(e) => handleDragStart(e, folder, 'folder')}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, folder)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, folder)}
            >
              <div className="item-icon folder-icon">
                <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M.54 3.87L.5 3a2 2 0 012-2h3.672a2 2 0 011.414.586l.828.828A2 2 0 009.828 3h3.982a2 2 0 011.992 2.181l-.637 7A2 2 0 0113.174 14H2.826a2 2 0 01-1.991-1.819l-.637-7a1.99 1.99 0 01.342-1.31z" />
                </svg>
              </div>
              <div className="item-info">
                <div className="item-name-row">
                  <span className="item-name">{folder.name}</span>
                  {folder.linked_projects && folder.linked_projects.length > 0 && (
                    <ProjectBadges projects={folder.linked_projects} maxVisible={2} size="sm" />
                  )}
                </div>
                <span className="item-meta">
                  {folder.document_count} file{folder.document_count !== 1 ? 's' : ''}
                  {folder.child_count > 0 && `, ${folder.child_count} folder${folder.child_count !== 1 ? 's' : ''}`}
                </span>
              </div>
              <span className="item-date">{formatDate(folder.created_at)}</span>
              {canManage && onFolderAction && (
                <button
                  className="item-actions"
                  onClick={(e) => {
                    e.stopPropagation();
                    onFolderAction('menu', folder, e.currentTarget);
                  }}
                  onContextMenu={(e) => e.stopPropagation()}
                  title="More actions"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <circle cx="2" cy="8" r="1.5" />
                    <circle cx="8" cy="8" r="1.5" />
                    <circle cx="14" cy="8" r="1.5" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Documents */}
      {documents.length > 0 && (
        <div className="file-explorer-section">
          {documents.map((doc) => (
            <div
              key={`doc-${doc.id}`}
              className="file-explorer-item document-item"
              onClick={() => onFileClick && onFileClick(doc)}
              onContextMenu={(e) => {
                if (onFileContextMenu) onFileContextMenu(e, doc);
              }}
              draggable={canManage && !!onMove}
              onDragStart={(e) => handleDragStart(e, doc, 'document')}
              onDragEnd={handleDragEnd}
            >
              <div className="item-icon file-icon">
                {getFileIcon(doc.content_type)}
              </div>
              <div className="item-info">
                <div className="item-name-row">
                  <span className="item-name">{doc.original_filename}</span>
                  {doc.linked_projects && doc.linked_projects.length > 0 && (
                    <ProjectBadges projects={doc.linked_projects} maxVisible={2} size="sm" />
                  )}
                </div>
                <span className="item-meta">
                  {doc.file_size_formatted} • Uploaded by {doc.uploaded_by_name || 'Unknown'}
                </span>
              </div>
              <span className="item-date">{formatDate(doc.created_at)}</span>
              {onFileAction && (
                <button
                  className="item-actions"
                  onClick={(e) => {
                    e.stopPropagation();
                    onFileAction('menu', doc, e.currentTarget);
                  }}
                  onContextMenu={(e) => e.stopPropagation()}
                  title="More actions"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <circle cx="2" cy="8" r="1.5" />
                    <circle cx="8" cy="8" r="1.5" />
                    <circle cx="14" cy="8" r="1.5" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      )}

    </div>
  );
};

FileExplorer.propTypes = {
  folders: PropTypes.arrayOf(PropTypes.object).isRequired,
  documents: PropTypes.arrayOf(PropTypes.object).isRequired,
  viewMode: PropTypes.oneOf(['list', 'grid']),
  canManage: PropTypes.bool.isRequired,
  onFolderClick: PropTypes.func.isRequired,
  onFileClick: PropTypes.func,
  onFolderContextMenu: PropTypes.func,
  onFileContextMenu: PropTypes.func,
  onFolderAction: PropTypes.func, // (action, folder) => void
  onFileAction: PropTypes.func, // (action, doc) => void
  onMove: PropTypes.func, // (itemType, itemId, targetFolderId) => Promise
  getFileIcon: PropTypes.func.isRequired,
  formatDate: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string,
};

export default FileExplorer;
